import React from 'react';
import { motion } from 'framer-motion'; // Using motion for animations

const FashionShoppingGuiltLanding = ({ goToInput, theme = "mint" }) => {
  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 mb-8">
      {/* SEO-optimized heading structure */}
      <h1 className="text-3xl font-bold text-center mb-6 dark:text-white">
        Fashion Shopping Guilt: Understand Your Wardrobe Emotions
      </h1>
      
      {/* Hero section with main CTA */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-6 mb-8">
        <div className="flex flex-col md:flex-row items-center">
          <div className="mb-6 md:mb-0 md:mr-6 text-center md:text-left">
            <h2 className="text-2xl font-bold mb-3 dark:text-white">Calculate Your Fashion Guilt Score</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Track your clothing purchases, understand your fashion spending habits, and reduce style-related buyer's remorse.
            </p>
            <button
              onClick={goToInput}
              className={`px-6 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
            >
              Track Your Fashion Purchase
            </button>
          </div>
          <div className="text-8xl">👗</div>
        </div>
      </div>
      
      {/* What causes fashion shopping guilt section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Why Do We Feel Guilty About Fashion Purchases?</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">💸</span>
              <div>
                <h3 className="font-bold dark:text-white">Price vs. Wear Ratio</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Spending on trendy items that rarely leave your closet creates financial guilt.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">😔</span>
              <div>
                <h3 className="font-bold dark:text-white">Emotional Shopping</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Buying clothing to boost mood or combat insecurity often leads to regret later.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🌍</span>
              <div>
                <h3 className="font-bold dark:text-white">Sustainability Concerns</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Fast fashion's environmental impact creates ethical guilt about consumption.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">👗</span>
              <div>
                <h3 className="font-bold dark:text-white">Closet Overflow</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Adding to an already full wardrobe triggers guilt about excess and waste.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* How Our Tool Helps */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">How Our Fashion Guilt Calculator Helps</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">1</span>
              Track Fashion Buying Triggers
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Identify emotional states that lead to impulse clothing purchases.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">2</span>
              Analyze Your Style-to-Spend Ratio
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Calculate cost-per-wear and evaluate the true value of your wardrobe choices.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">3</span>
              Get Tailored Fashion Advice
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Receive personalized tips for building a sustainable, guilt-free wardrobe.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">4</span>
              Build a Conscious Closet
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Learn to make mindful fashion choices that balance style, budget, and ethics.
            </p>
          </div>
        </div>
      </section>
      
      {/* Fashion Shopper Types Section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Discover Your Fashion Shopper Type</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">👑</span>
              <h3 className="font-bold mt-2 dark:text-white">Trend Chaser</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              Always buying the latest styles, your closet is full of barely-worn "must-haves" that quickly fall out of rotation.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">💎</span>
              <h3 className="font-bold mt-2 dark:text-white">Luxury Lover</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You invest in high-end pieces to feel special, but often question if they're worth the splurge afterward.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🛍️</span>
              <h3 className="font-bold mt-2 dark:text-white">Sale Seeker</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You can't resist a good deal, buying items you don't need simply because they're discounted.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🧠</span>
              <h3 className="font-bold mt-2 dark:text-white">Mindful Dresser</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You carefully consider each addition to your wardrobe, balancing quality, ethics, and personal style.
            </p>
          </div>
        </div>
      </section>
      
      {/* Fashion Guilt Stats */}
      <section className="mb-8 bg-gray-50 dark:bg-gray-700 rounded-xl p-5">
        <h2 className="text-xl font-bold mb-4 dark:text-white">Fashion Shopping By The Numbers</h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold dark:text-white">80%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">of clothing items are worn fewer than 3 times</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">$3,000</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">average annual fashion spending per person</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">73%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">of shoppers experience fashion buyer's remorse</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">$400B</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">value of unworn clothing globally</p>
          </div>
        </div>
      </section>
      
      {/* Testimonials section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">How Others Improved Their Fashion Choices</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "After tracking my clothing purchases for a month, I realized I was spending over $300 on trend pieces I barely wore. Now I invest in timeless items I actually love."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Maya K.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Reformed Trend Chaser</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "I used to buy new outfits for every occasion, leaving my closet full of one-time wears. The guilt calculator helped me identify my emotional shopping triggers."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">James L.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Mindful Dresser</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Section - important for SEO */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Common Questions About Fashion Shopping Guilt</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Why do I feel guilty after buying clothes?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Fashion guilt often stems from financial concerns, sustainability worries, closet overcrowding, or the emotional high of shopping followed by the reality of an unnecessary purchase. Our tool helps identify your specific guilt triggers.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How can I build a more sustainable wardrobe?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Start by tracking your current clothing habits with our fashion guilt calculator. Then focus on buying quality over quantity, considering cost-per-wear, choosing ethical brands, and implementing a waiting period before purchases.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">What is a healthy amount to spend on clothing?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Financial experts recommend spending 5-10% of your income on clothing. However, the "right" amount varies based on your lifestyle, needs, and values. Our tool helps you determine what spending pattern feels good both emotionally and financially.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How can I stop impulse buying fashion items?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Track your purchases with our fashion guilt calculator to identify triggers. Then implement strategies like a 24-hour rule for purchases, unsubscribing from retail emails, creating a carefully planned wardrobe, and setting specific shopping goals.
            </p>
          </div>
        </div>
      </section>
      
      {/* CTA section */}
      <section className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 rounded-xl p-6 text-center">
        <h2 className="text-2xl font-bold mb-3 dark:text-white">Ready to Build a Guilt-Free Wardrobe?</h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          Start tracking your fashion purchases and transform your relationship with clothing.
        </p>
        <button
          onClick={goToInput}
          className={`px-8 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
        >
          Calculate Your Fashion Guilt Now
        </button>
      </section>
      
      {/* Rich SEO footer content */}
      <footer className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <h2 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-200">About Fashion Shopping Guilt</h2>
          <p className="mb-3">
            Fashion shopping guilt is the emotional discomfort experienced after clothing purchases, often stemming from financial concerns, ethical worries about fast fashion, or the realization that new items aren't necessary additions to your wardrobe.
          </p>
          <p className="mb-3">
            Our Fashion Guilt Calculator helps you understand and address these feelings by tracking your emotional state during purchases, analyzing your spending patterns, and providing personalized strategies to build a more mindful and satisfying wardrobe.
          </p>
          <p>
            Whether you're dealing with closet overflow, fashion impulse buying, or concerns about your clothing's environmental impact, our free tool helps you develop healthier fashion habits and reduce shopping guilt once and for all.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default FashionShoppingGuiltLanding;
